import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Lock, ArrowLeft, ArrowRight, KeyRound } from "lucide-react";

/**
 * /reset-password?token=XYZ — present new-password form, submit + auto-sign-in.
 *
 * The backend returns the same {token,user} shape as /auth/login on success,
 * so we drop it straight into the AppContext and route the user into the app.
 */
export default function ResetPassword() {
    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { setSession } = useApp(); // we'll add this helper below
    const queryToken = params.get("token") || "";

    const [token, setToken] = useState(queryToken);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
        if (password !== confirm) { toast.error("Passwords don't match."); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${backend}/api/auth/reset-password`, { token, password });
            if (data?.token) {
                try { await setSession?.(data); } catch { /* fallback below */ }
                // Bare-minimum fallback if setSession isn't wired yet — store the
                // bearer token in localStorage so the rest of the app picks it up.
                if (typeof window !== "undefined") {
                    window.localStorage.setItem("owed:token", data.token);
                }
            }
            toast.success("Password reset — you're signed in.");
            navigate("/app/dashboard", { replace: true });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't reset password. The link may have expired.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-hero">
            <div className="mx-auto max-w-md px-5 sm:px-8 py-10 sm:py-16">
                <Link to="/auth" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="reset-back-link">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
                <Card className="mt-6 p-8 sm:p-10 border-border shadow-elegant" data-testid="reset-card">
                    <p className="text-xs font-mono uppercase tracking-[0.22em] text-muted-foreground">Account recovery</p>
                    <h1 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight">Set a new password</h1>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">Once you save, you'll be signed in automatically.</p>

                    <form onSubmit={submit} className="mt-6 space-y-4">
                        {!queryToken && (
                            <div>
                                <Label htmlFor="token" className="text-sm">Reset token</Label>
                                <div className="relative mt-2">
                                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input id="token" required value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste the token from the reset link" className="pl-9 h-11 rounded-xl font-mono text-xs" data-testid="reset-token-input" />
                                </div>
                            </div>
                        )}
                        <div>
                            <Label htmlFor="pw" className="text-sm">New password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 h-11 rounded-xl" autoComplete="new-password" data-testid="reset-password-input" />
                            </div>
                            <p className="mt-1.5 text-xs text-muted-foreground">At least 6 characters.</p>
                        </div>
                        <div>
                            <Label htmlFor="pw2" className="text-sm">Confirm new password</Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input id="pw2" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="pl-9 h-11 rounded-xl" autoComplete="new-password" data-testid="reset-confirm-input" />
                            </div>
                        </div>
                        <Button type="submit" disabled={loading || !token || !password || !confirm} className="w-full rounded-full bg-foreground text-background hover:bg-foreground/90 h-11" data-testid="reset-submit-btn">
                            {loading ? "Resetting…" : "Set new password & sign in"}
                            <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                    </form>
                </Card>
            </div>
        </div>
    );
}
